import { lowerCase, startCase } from 'lodash-es'
import { ComponentProps, JSX, useMemo, useState } from 'react'
import {
  LuBan,
  LuCalendarClock,
  LuChartPie,
  LuCircleArrowRight,
  LuCircleCheck,
  LuRoute,
  LuX,
} from 'react-icons/lu'

import { toaster } from '@/components/Toaster'
import { useUpdateTaskAssignment } from '@/feature/execution-board/api'

import { SelectDefault, SelectOption } from '@/lib/core/components/Select'

import { TaskDefinition } from '../../types/task'
import { TaskStatus } from '../CreateTaskModal'
import styles from './TaskStatusDropdown.module.css'

interface TaskStatusDropdownProps {
  task?: TaskDefinition
  currentStatus: string
  disabled?: boolean
  onStatusUpdated?: (taskId: string, newStatus: TaskStatus) => void
  className?: string
  testId?: string
  displayOnly?: boolean
}

export const STATUS_OPTIONS: SelectOption<TaskStatus>[] = Object.values(TaskStatus).map(
  (status) => ({
    value: status,
    label: startCase(lowerCase(status)),
  }),
)

function getControlBorderColor(
  hasError: boolean,
  isFocused: boolean,
  defaultColor: string | undefined,
) {
  if (hasError) return '#ef4444'
  if (isFocused) return '#3b82f6'
  return defaultColor ?? '#d1d5db'
}

function getControlBoxShadow(
  hasError: boolean,
  isFocused: boolean,
  defaultShadow: string | undefined,
) {
  if (hasError) return '0 0 0 1px #ef4444'
  if (isFocused) return '0 0 0 1px #3b82f6'
  return defaultShadow ?? 'none'
}

function getOptionBackground(
  isSelected: boolean,
  isFocused: boolean,
  defaultBg: string | undefined,
) {
  if (isSelected) return '#3b82f6'
  if (isFocused) return '#eff6ff'
  return defaultBg ?? '#ffffff'
}

const STATUS_COLORS = {
  [TaskStatus.SCHEDULED]: '#4ABED1',
  [TaskStatus.IN_PROGRESS]: '#92E663',
  [TaskStatus.DELAYED]: '#FF8484',
  [TaskStatus.COMPLETED]: '#ECECEC',
  [TaskStatus.REJECTED]: '#FF9090',
  [TaskStatus.REQUESTED]: '#FFE373',
  [TaskStatus.CANCELLED]: '#D4D4D4',
}

const STATUS_ICONS = {
  [TaskStatus.SCHEDULED]: <LuCalendarClock size={16} color="#404040" />,
  [TaskStatus.IN_PROGRESS]: <LuRoute size={16} color="#404040" />,
  [TaskStatus.DELAYED]: <LuChartPie size={16} color="#404040" />,
  [TaskStatus.COMPLETED]: <LuCircleCheck size={16} color="#404040" />,
  [TaskStatus.REJECTED]: <LuX size={16} color="#404040" />,
  [TaskStatus.REQUESTED]: <LuCircleArrowRight size={16} color="#404040" />,
  [TaskStatus.CANCELLED]: <LuBan size={16} color="#404040" />,
}

const VALID_STATE_TRANSITIONS = {
  [TaskStatus.REQUESTED]: [TaskStatus.REJECTED, TaskStatus.SCHEDULED, TaskStatus.CANCELLED],
  [TaskStatus.SCHEDULED]: [
    TaskStatus.REQUESTED,
    TaskStatus.IN_PROGRESS,
    TaskStatus.COMPLETED,
    TaskStatus.CANCELLED,
  ],
  [TaskStatus.IN_PROGRESS]: [TaskStatus.DELAYED, TaskStatus.COMPLETED],
  [TaskStatus.DELAYED]: [TaskStatus.IN_PROGRESS, TaskStatus.COMPLETED],
  [TaskStatus.COMPLETED]: [],
  [TaskStatus.REJECTED]: [],
  [TaskStatus.CANCELLED]: [],
}

function getStatusBackground(status: TaskStatus) {
  return STATUS_COLORS[status]
}

function getStatusIcon(status: TaskStatus): JSX.Element {
  return STATUS_ICONS[status]
}

export const TaskStatusDropdown = ({
  task,
  currentStatus,
  disabled = false,
  onStatusUpdated,
  className,
  testId,
  displayOnly = false,
}: TaskStatusDropdownProps) => {
  const {
    trigger: updateTaskStatus,
    isMutating: isTaskUpdating,
    error: updateError,
  } = useUpdateTaskAssignment(task?.taskDefinition.id ?? '', task?.id ?? '')

  const [updatedStatus, setUpdatedStatus] = useState<TaskStatus | undefined>()

  const currentOption = useMemo(() => {
    if (updatedStatus) {
      STATUS_OPTIONS.find((option) => option.value === updatedStatus)
    }
    return STATUS_OPTIONS.find((option) => option.value === currentStatus)
  }, [currentStatus, updatedStatus])

  const availableStatusOptions = useMemo(() => {
    const availableStatues: TaskStatus[] = VALID_STATE_TRANSITIONS[currentStatus as TaskStatus]
    return STATUS_OPTIONS.filter((option: SelectOption<TaskStatus>) =>
      availableStatues.includes(option.value),
    )
  }, [currentStatus])

  const isCompleted = currentStatus === TaskStatus.COMPLETED
  const isDisabled = disabled || isTaskUpdating
  const hasError = Boolean(updateError)
  const computedTestId = testId ?? `task-status-dropdown-${task?.taskDefinition.id ?? 'display'}`

  if (displayOnly) {
    return (
      <div
        className={`${styles.statusDisplay} ${className ?? ''}`.trim()}
        data-testid={computedTestId}
      >
        <div
          className={styles.statusIcon}
          style={{
            backgroundColor: getStatusBackground(currentStatus as TaskStatus),
            opacity: isCompleted ? 0.5 : 1,
          }}
        >
          {getStatusIcon(currentStatus as TaskStatus)}
        </div>
        <span className={styles.statusLabel}>{startCase(lowerCase(currentStatus))}</span>
      </div>
    )
  }

  return (
    <div data-testid={`${computedTestId}-container`}>
      <div
        className={`${styles.statusDropdown} ${className ?? ''}`.trim()}
        data-testid={computedTestId}
      >
        <div
          className={styles.statusIcon}
          style={{
            backgroundColor: getStatusBackground(currentStatus as TaskStatus),
            opacity: isCompleted ? 0.5 : 1,
          }}
        >
          {getStatusIcon(currentStatus as TaskStatus)}
        </div>
        <SelectDefault
          aria-label="Task status"
          aria-invalid={hasError}
          aria-describedby={hasError ? `${computedTestId}-error` : undefined}
          data-testid={`${computedTestId}-select`}
          options={availableStatusOptions}
          value={currentOption}
          onChange={
            (async (newStatus: SelectOption<TaskStatus>) => {
              if (!task || !newStatus || newStatus.value === currentStatus || isTaskUpdating) return
              try {
                setUpdatedStatus(newStatus.value)
                await updateTaskStatus({ taskId: task.taskDefinition.id, status: newStatus.value })
                toaster({
                  type: 'success',
                  message: `Task status updated to "${startCase(lowerCase(newStatus.value))}"`,
                })
                onStatusUpdated?.(task.taskDefinition.id, newStatus.value)
              } catch (err) {
                console.error('Failed to update task status:', err)
                toaster({
                  type: 'error',
                  message: 'Failed to update task status. Please try again.',
                })
              }
            }) as unknown as ComponentProps<typeof SelectDefault>['onChange']
          }
          components={{
            IndicatorsContainer: () => null,
          }}
          isDisabled={isDisabled}
          isLoading={isTaskUpdating}
          placeholder="Select status..."
          isSearchable={true}
          isClearable={false}
          noOptionsMessage={() => 'No status options available'}
          loadingMessage={() => 'Updating status...'}
          leftAdorment={true}
          menuPortalTarget={document.body}
          styles={{
            container: (provided) => ({
              ...provided,
              minWidth: '140px',
            }),
            control: (provided, state) => ({
              ...provided,
              minHeight: '40px',
              fontSize: '14px',
              cursor: state.isDisabled ? 'not-allowed' : 'pointer',
              borderColor: hasError
                ? getControlBorderColor(hasError, state.isFocused, provided.borderColor as string)
                : '#E0E0E0',
              boxShadow: getControlBoxShadow(
                hasError,
                state.isFocused,
                provided.boxShadow as string,
              ),
              borderRadius: '4px',
              backgroundColor: 'transparent',
              paddingLeft: '28px',
              '&:hover': {
                borderColor: hasError ? '#ef4444' : '#9CA3AF',
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
              },
            }),
            menuPortal: (provided) => ({
              ...provided,
              zIndex: 100,
            }),
            menu: (provided) => ({
              ...provided,
            }),
            singleValue: (provided) => ({
              ...provided,
              color: isCompleted ? '#9CA3AF' : '#404040',
            }),
            option: (provided, state) => ({
              ...provided,
              fontSize: '14px',
              zIndex: 2,
              backgroundColor: getOptionBackground(
                state.isSelected,
                state.isFocused,
                provided.backgroundColor as string,
              ),
            }),
          }}
        />
        {hasError && (
          <div
            id={`${computedTestId}-error`}
            className={styles.errorMessage}
            role="alert"
            data-testid={`${computedTestId}-error-message`}
          >
            Status update failed
          </div>
        )}
      </div>
    </div>
  )
}
